import { PORTS, SHIPPING, BOUNDS, NIGERIA_BOUNDS, COUNTRY_LABELS, STATE_LABELS, COUNTRIES, FACILITY_TYPES, ECOWAS } from './data';
import { lane } from './geo';
const MAPLIBRE = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl';
let loading = null;

/** Load MapLibre GL JS (about 800 KB) only when the map page is opened. */
export function loadMapLibre() {
  if (window.maplibregl) return Promise.resolve(window.maplibregl);
  loading ??= new Promise((resolve, reject) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = MAPLIBRE + '.css';
    document.head.appendChild(css);
    const js = document.createElement('script');
    js.src = MAPLIBRE + '.js';
    js.onload = () => window.maplibregl ? resolve(window.maplibregl) : reject(new Error('MapLibre did not load'));
    js.onerror = () => {
      loading = null;
      reject(new Error('MapLibre could not be downloaded'));
    };
    document.head.appendChild(js);
  });
  return loading;
}

// MapTiler styles for the Map / Satellite / Terrain switch
// Land only = pure satellite imagery (no labels, roads or borders drawn on it)
const STYLES = {
  map: 'streets-v2',
  satellite: 'hybrid',
  terrain: 'outdoor-v2',
  land: 'satellite'
};

// "Land only" hides these parts of the base map (our own layers are kept)
const LAND_ONLY_HIDE = ['boundary', 'transportation', 'transportation_name', 'building', 'poi', 'aeroway', 'housenumber', 'place', 'mountain_peak', 'park', 'aerodrome_label'];

/** 232,679,478 -> "232.7M" */
export const short = n => n >= 1e9 ? (n / 1e9).toFixed(1) + 'B' : n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? Math.round(n / 1e3) + 'K' : String(Math.round(n));

// Moving dashes: stepping through these patterns makes the dashes travel along each line,
// in the direction the line is drawn (OpenStreetMap draws rivers downstream, so: with the flow)
const DASHES = [[0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0], [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5]];

/**
 * Everything that touches the map: tiles and styles, rivers/roads/rail from the map data,
 * shipping lanes, markers, selection and the flow animation.
 */
export default class MapController {
  constructor(el, key, _ref) {
    let {
      onSelect,
      onError,
      layers,
      ecowasOnly,
      bounds,
      cooperative = true
    } = _ref;
    this.el = el;
    this.key = key;
    this.onSelect = onSelect;
    this.cooperative = cooperative; // two fingers to pan (on a page you scroll); off in the app's Atlas screen
    this.onError = onError;
    // the page passes its own starting choices, so the two never disagree
    this.layers = {
      resources: true,
      facilities: true,
      roads: false,
      rail: true,
      shipping: true,
      ports: true,
      rivers: true,
      population: true,
      ...(layers || {})
    };
    this.landOnly = false;
    this.ecowasOnly = ecowasOnly !== undefined ? !!ecowasOnly : true; // mask everything outside the 12 members
    this.startBounds = bounds || (this.ecowasOnly ? BOUNDS : NIGERIA_BOUNDS);
    this.ecowas = null; // { mask, outline } from /resource-map/ecowas
    this.popMarkers = [];
    this.items = [];
    this.visible = new Set();
    this.markers = {};
    this.animating = false;
    this.step = 0;
  }
  async init(style) {
    if (style === void 0) {
      style = 'terrain';
    }
    this.style = style;
    this.landOnly = style === 'land';
    const gl = await loadMapLibre();
    this.gl = gl;
    // MapLibre gets its own element, created here, so page redraws never touch it
    this.box = document.createElement('div');
    this.box.className = 'RMMap-gl';
    this.el.appendChild(this.box);
    this.map = new gl.Map({
      container: this.box,
      style: this.styleUrl(style),
      bounds: this.startBounds,
      fitBoundsOptions: {
        padding: 20
      },
      maxBounds: [[-40, -12], [32, 30]],
      preserveDrawingBuffer: true,
      attributionControl: {
        compact: true
      },
      cooperativeGestures: this.cooperative
    });
    this.map.addControl(new gl.NavigationControl({
      showCompass: true,
      visualizePitch: true
    }), 'top-right');
    // Full screen: a button under the zoom buttons (the page decides what goes full screen)
    const ctl = this;
    this.map.addControl({
      onAdd() {
        const box = document.createElement('div');
        box.className = 'maplibregl-ctrl maplibregl-ctrl-group';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'RMFullscreenBtn';
        b.addEventListener('click', () => ctl.onFullscreen && ctl.onFullscreen());
        box.appendChild(b);
        ctl.fullscreenBtn = b;
        ctl.setFullscreen(false);
        return box;
      },
      onRemove() {}
    }, 'top-right');
    this.map.addControl(new gl.ScaleControl({
      unit: 'metric'
    }), 'bottom-right');
    this.map.on('style.load', () => {
      this.styleLoaded = true;
      if (this.landOnly) this.hideBaseDetail();
      this.addOverlays();
    });
    this.map.on('zoom', () => this.applyPopulation());
    this.map.on('rotateend', () => this.onRotate && this.onRotate(this.isSouthUp()));
    this.map.on('error', e => {
      const err = e && e.error || e;
      const msg = String(err && err.message || err || '');
      const status = err && err.status;
      console.warn('[Resource map]', msg, err); // never hide map errors
      if (status === 401 || status === 403 || /\b(401|403)\b|forbidden|unauthori[sz]ed|invalid key/i.test(msg)) {
        this.onError('MapTiler refused the map key (' + (status || 'access denied') + '). Check the key in Admin → Voucha Resource Map, and that its allowed HTTP origins include ' + window.location.hostname + '.');
      } else if (!this.styleLoaded && /style|json|fetch|network/i.test(msg)) {
        this.onError('The map style could not be loaded: ' + msg);
      }
    });
    // If the style never arrives, say so instead of staying blank
    this.watchdog = setTimeout(() => {
      if (!this.styleLoaded) this.onError('The map did not load within 20 seconds. Check the MapTiler key and its allowed HTTP origins, then refresh.');
    }, 20000);
    this.map.once('load', () => clearTimeout(this.watchdog));
    // Keep the map sized to its box (e.g. if the page lays out after the map is created)
    requestAnimationFrame(() => this.map && this.map.resize());
    if (window.ResizeObserver) {
      this.resizer = new window.ResizeObserver(() => this.map && this.map.resize());
      this.resizer.observe(this.box);
    }
    // markers are created by setItems() once the items have loaded from the database
  }
  styleUrl(name) {
    return 'https://api.maptiler.com/maps/' + (STYLES[name] || STYLES.terrain) + '/style.json?key=' + encodeURIComponent(this.key);
  }
  setStyle(name) {
    this.style = name;
    this.landOnly = name === 'land';
    if (this.map) this.map.setStyle(this.styleUrl(name)); // our layers are added again on "style.load"
  }

  /** "Land only": hide borders, labels, roads, buildings and points of interest from the base map */
  hideBaseDetail() {
    for (const layer of this.map.getStyle().layers || []) {
      if (layer.id.startsWith('rm-')) continue;
      if (layer.type === 'symbol' || layer.type === 'line' || LAND_ONLY_HIDE.includes(layer['source-layer'])) {
        this.map.setLayoutProperty(layer.id, 'visibility', 'none');
      }
    }
  }

  /**
   * Population labels: countries when zoomed out, Nigerian states when zoomed in on Nigeria.
   * data = { countries: { SN: {value, year, source} }, states: { kano: {name, value, year, source} } }
   */
  setPopulation(data) {
    var _this = this;
    this.popMarkers.forEach(p => p.marker.remove());
    this.popMarkers = [];
    const add = function (lat, lng, name, d, level, code) {
      if (code === void 0) {
        code = 'NG';
      }
      const el = document.createElement('div');
      el.className = 'RMPop is-' + level;
      el.innerHTML = '<b></b><span></span>';
      el.querySelector('b').textContent = name;
      el.querySelector('span').textContent = short(d.value);
      el.title = name + ': ' + Math.round(d.value).toLocaleString('en-US') + ' people (' + d.source + ', ' + d.year + ')';
      _this.popMarkers.push({
        level,
        code,
        el,
        marker: new _this.gl.Marker({
          element: el
        }).setLngLat([lng, lat]).addTo(_this.map)
      });
    };
    for (const [code, d] of Object.entries(data && data.countries || {})) {
      const at = COUNTRY_LABELS[code];
      if (at) add(at[0], at[1], (COUNTRIES.find(c => c[0] === code) || [])[1] || code, d, 'country', code);
    }
    for (const [slug, d] of Object.entries(data && data.states || {})) {
      const at = STATE_LABELS[slug];
      if (at) add(at[0], at[1], d.name, d, 'state');
    }
    this.applyPopulation();
  }

  /** Which population labels to show at this zoom */
  applyPopulation() {
    if (!this.map) return;
    const zoomedIn = this.map.getZoom() >= 5.6;
    for (const p of this.popMarkers) {
      const show = this.layers.population && this.inView(p.code) && (p.level === 'state' ? zoomedIn : !zoomedIn || p.el.querySelector('b').textContent !== 'Nigeria');
      p.el.style.display = show ? '' : 'none';
    }
  }

  /** Rivers, lakes, roads and rail from MapTiler's data, plus the shipping lanes; drawn under the place names */
  addOverlays() {
    const map = this.map;
    if (!map.getSource('rm-omt')) {
      map.addSource('rm-omt', {
        type: 'vector',
        url: 'https://api.maptiler.com/tiles/v3/tiles.json?key=' + encodeURIComponent(this.key)
      });
    }
    if (!map.getSource('rm-lanes')) map.addSource('rm-lanes', {
      type: 'geojson',
      data: this.lanesData()
    });
    if (!map.getSource('rm-railproj')) map.addSource('rm-railproj', {
      type: 'geojson',
      data: this.railData()
    });
    if (!map.getSource('rm-draft')) map.addSource('rm-draft', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    const labels = (map.getStyle().layers || []).find(l => l.type === 'symbol');
    const before = labels ? labels.id : undefined;
    const add = layer => !map.getLayer(layer.id) && map.addLayer(layer, before);
    const z = (a, b) => ['interpolate', ['linear'], ['zoom'], 4, a, 10, b];
    add({
      id: 'rm-lakes',
      type: 'fill',
      source: 'rm-omt',
      'source-layer': 'water',
      filter: ['==', ['get', 'class'], 'lake'],
      paint: {
        'fill-color': '#3b9be0',
        'fill-opacity': 0.55
      }
    });
    add({
      id: 'rm-rivers',
      type: 'line',
      source: 'rm-omt',
      'source-layer': 'waterway',
      filter: ['==', ['get', 'class'], 'river'],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': '#2b8cd6',
        'line-width': z(1.4, 5)
      }
    });
    add({
      id: 'rm-rivers-flow',
      type: 'line',
      source: 'rm-omt',
      'source-layer': 'waterway',
      filter: ['==', ['get', 'class'], 'river'],
      layout: {
        'line-cap': 'butt'
      },
      paint: {
        'line-color': '#e8f6ff',
        'line-width': z(0.8, 2.5),
        'line-dasharray': DASHES[0]
      }
    });
    add({
      id: 'rm-roads',
      type: 'line',
      source: 'rm-omt',
      'source-layer': 'transportation',
      filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary']]],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e8902e',
        'line-width': z(1, 3.5)
      }
    });
    add({
      id: 'rm-rail',
      type: 'line',
      source: 'rm-omt',
      'source-layer': 'transportation',
      filter: ['==', ['get', 'class'], 'rail'],
      paint: {
        'line-color': '#2f3a35',
        'line-width': z(1.4, 3),
        'line-dasharray': [1, 0.6]
      }
    });

    // Rail projects from the database: solid when operational, dashed under construction, dotted when planned
    const rail = (id, status, paint) => add({
      id,
      type: 'line',
      source: 'rm-railproj',
      filter: ['==', ['get', 'status'], status],
      layout: {
        'line-cap': 'round'
      },
      paint: {
        'line-width': z(2.5, 5),
        ...paint
      }
    });
    rail('rm-railproj-op', 'operational', {
      'line-color': '#8b1e3f'
    });
    rail('rm-railproj-con', 'construction', {
      'line-color': '#e0701b',
      'line-dasharray': [2, 1.2]
    });
    rail('rm-railproj-plan', 'planned', {
      'line-color': '#7b7f86',
      'line-dasharray': [0.6, 1.4]
    });
    rail('rm-railproj-closed', 'closed', {
      'line-color': '#9aa0a6',
      'line-opacity': 0.5
    });
    // On top of everything else: the ECOWAS mask and outline, then shipping lanes and the editor's draft
    const top = layer => !map.getLayer(layer.id) && map.addLayer(layer);
    if (this.ecowas) {
      if (!map.getSource('rm-mask')) map.addSource('rm-mask', {
        type: 'geojson',
        data: this.ecowas.mask
      });
      if (!map.getSource('rm-outline')) map.addSource('rm-outline', {
        type: 'geojson',
        data: this.ecowas.outline
      });
      // The mask may arrive after the other layers: always slot it in just below the shipping lanes
      const under = map.getLayer('rm-shipping') ? 'rm-shipping' : undefined;
      if (!map.getLayer('rm-mask')) map.addLayer({
        id: 'rm-mask',
        type: 'fill',
        source: 'rm-mask',
        paint: {
          'fill-color': this.maskColor(),
          'fill-opacity': 1
        }
      }, under);
      if (!map.getLayer('rm-outline')) map.addLayer({
        id: 'rm-outline',
        type: 'line',
        source: 'rm-outline',
        layout: {
          'line-join': 'round'
        },
        paint: {
          'line-color': '#1b4540',
          'line-width': z(1, 2),
          'line-opacity': 0.55
        }
      }, under);
      // Major rivers as centrelines pointing downstream (Natural Earth), so the flow shows even where the
      // map draws a wide river (like the Niger) as a water area. Under the mask: only ECOWAS rivers show.
      if (this.ecowas.flow) {
        if (!map.getSource('rm-flow')) map.addSource('rm-flow', {
          type: 'geojson',
          data: this.ecowas.flow
        });
        if (!map.getLayer('rm-flow-base')) map.addLayer({
          id: 'rm-flow-base',
          type: 'line',
          source: 'rm-flow',
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#2b8cd6',
            'line-width': z(1.8, 5)
          }
        }, 'rm-mask');
        if (!map.getLayer('rm-flow-anim')) map.addLayer({
          id: 'rm-flow-anim',
          type: 'line',
          source: 'rm-flow',
          paint: {
            'line-color': '#e8f6ff',
            'line-width': z(1, 2.6),
            'line-dasharray': DASHES[0]
          }
        }, 'rm-mask');
      }
    }
    top({
      id: 'rm-shipping',
      type: 'line',
      source: 'rm-lanes',
      paint: {
        'line-color': '#1d5fd1',
        'line-width': 1.8,
        'line-opacity': 0.85,
        'line-dasharray': DASHES[0]
      }
    });
    // A member's plan: lines coloured by type, notes as dots (their text shows as labels)
    if (!map.getSource('rm-plan')) map.addSource('rm-plan', {
      type: 'geojson',
      data: this.planData()
    });
    top({
      id: 'rm-plan-lines',
      type: 'line',
      source: 'rm-plan',
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-width': z(3, 6),
        'line-color': ['match', ['get', 'type'], 'road', '#e8590c', 'logistics', '#7b2cbf', 'trade', '#0b7285', '#495057'],
        'line-dasharray': ['match', ['get', 'type'], 'logistics', ['literal', [2, 1]], 'trade', ['literal', [1, 1]], ['literal', [1, 0]]]
      }
    });
    top({
      id: 'rm-plan-notes',
      type: 'circle',
      source: 'rm-plan',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 7,
        'circle-color': '#f59f00',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2
      }
    });
    top({
      id: 'rm-draft-line',
      type: 'line',
      source: 'rm-draft',
      paint: {
        'line-color': '#d01e6e',
        'line-width': 3,
        'line-dasharray': [1.5, 1]
      }
    });
    top({
      id: 'rm-draft-pts',
      type: 'circle',
      source: 'rm-draft',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 5,
        'circle-color': '#d01e6e',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2
      }
    });
    // Outside the chosen state or LGA: a dark veil, leaving the place itself clear
    if (!map.getSource('rm-area-mask')) map.addSource('rm-area-mask', {
      type: 'geojson',
      data: this.maskData()
    });
    top({
      id: 'rm-area-mask',
      type: 'fill',
      source: 'rm-area-mask',
      paint: {
        'fill-color': '#0b1512',
        'fill-opacity': 0.55
      }
    });
    // The chosen state or LGA: a light fill and an outline (re-added after every style change)
    if (!map.getSource('rm-area')) map.addSource('rm-area', {
      type: 'geojson',
      data: this.areaData()
    });
    top({
      id: 'rm-area-fill',
      type: 'fill',
      source: 'rm-area',
      paint: {
        'fill-color': '#1f3d35',
        'fill-opacity': 0.06
      }
    });
    top({
      id: 'rm-area-line',
      type: 'line',
      source: 'rm-area',
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#1f3d35',
        'line-width': z(1.6, 3),
        'line-dasharray': [2, 1]
      }
    });
    if (!this.railClicks) {
      this.railClicks = true;
      for (const id of ['rm-railproj-op', 'rm-railproj-con', 'rm-railproj-plan', 'rm-railproj-closed']) {
        map.on('click', id, e => {
          if (this.picking) return;
          const item = this.items.find(i => i.dbId === (e.features[0] && e.features[0].properties.dbId));
          if (item) this.onSelect(item);
        });
        map.on('mouseenter', id, () => !this.picking && (map.getCanvas().style.cursor = 'pointer'));
        map.on('mouseleave', id, () => !this.picking && (map.getCanvas().style.cursor = ''));
      }
    }
    this.applyLayers();
  }
  applyLayers() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const vis = (id, on) => this.map.getLayer(id) && this.map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    const lines = !this.landOnly; // "Land only" removes every line, whatever the chips say
    vis('rm-lakes', lines && this.layers.rivers); // Land only: natural imagery, no lake tint either
    vis('rm-rivers', lines && this.layers.rivers);
    vis('rm-rivers-flow', lines && this.layers.rivers && this.animating);
    vis('rm-flow-base', lines && this.layers.rivers);
    vis('rm-flow-anim', lines && this.layers.rivers && this.animating);
    vis('rm-roads', lines && this.layers.roads);
    vis('rm-rail', lines && this.layers.rail);
    vis('rm-shipping', lines && this.layers.shipping);
    ['rm-railproj-op', 'rm-railproj-con', 'rm-railproj-plan', 'rm-railproj-closed'].forEach(id => vis(id, lines && this.layers.rail));
    vis('rm-mask', this.ecowasOnly);
    vis('rm-outline', lines && this.ecowasOnly);
  }

  /** Mask colour for the current style */
  /** Non-member land: black over imagery, blank (off-white) on the drawn maps; the sea is never covered */
  maskColor() {
    return this.style === 'satellite' || this.style === 'land' ? '#000000' : '#f4f4f2';
  }

  /** The ECOWAS mask data (loaded once by the page) */
  setEcowas(data) {
    this.ecowas = data;
    if (this.map && this.map.isStyleLoaded()) this.addOverlays();
    this.applyMarkers();
    this.applyPopulation();
  }
  setEcowasOnly(on) {
    this.ecowasOnly = on;
    this.applyLayers();
    this.applyMarkers();
    this.applyPopulation();
    if (this.map) this.map.fitBounds(on ? BOUNDS : [[-26, 3.5], [16, 21]], {
      padding: 20,
      duration: 700
    });
  }

  /** Inside the current view? (non-members are hidden while "ECOWAS only" is on) */
  inView(country) {
    return !this.ecowasOnly || !country || ECOWAS.includes(country);
  }

  /** Port positions: from the database, falling back to the built-in list */
  portsById() {
    const ports = this.items.filter(i => i.kind === 'port');
    return Object.fromEntries((ports.length ? ports : PORTS).map(p => [p.id, p]));
  }
  lanesData() {
    const byId = this.portsById();
    return {
      type: 'FeatureCollection',
      features: SHIPPING.filter(_ref2 => {
        let [a, b] = _ref2;
        return byId[a] && byId[b];
      }).map(_ref3 => {
        let [a, b] = _ref3;
        return {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: lane(byId[a], byId[b])
          }
        };
      })
    };
  }
  railData() {
    return {
      type: 'FeatureCollection',
      features: this.items.filter(i => i.kind === 'rail' && Array.isArray(i.geometry) && i.geometry.length > 1).map(i => ({
        type: 'Feature',
        properties: {
          dbId: i.dbId,
          status: i.status
        },
        geometry: {
          type: 'LineString',
          coordinates: i.geometry
        }
      }))
    };
  }
  setLayer(name, on) {
    this.layers[name] = on;
    this.applyLayers();
    this.applyMarkers();
    this.applyPopulation();
  }

  /** Everything on the map, from the database. Rebuilds markers, lanes and rail lines. */
  setItems(items) {
    this.items = items;
    Object.values(this.markers).forEach(m => m.marker.remove());
    this.markers = {};
    const gl = this.gl;
    for (const s of items) {
      if (s.kind === 'rail' || s.lat == null) continue;
      const el = document.createElement('button');
      el.type = 'button';
      el.dataset.id = s.id;
      if (s.kind === 'port') {
        el.className = 'RMMarker is-port is-' + s.status;
        el.innerHTML = '<i class="fas fa-anchor" aria-hidden="true"></i><b></b>';
        el.querySelector('b').textContent = s.name;
        el.title = 'Port of ' + s.name;
      } else if (s.kind === 'facility') {
        const t = FACILITY_TYPES[s.type] || FACILITY_TYPES.other;
        el.className = 'RMMarker is-facility is-' + s.type + ' is-' + s.status;
        el.innerHTML = '<i class="' + t.icon + '" aria-hidden="true"></i>';
        el.title = s.name + ' · ' + t.label;
      } else {
        el.className = 'RMMarker ' + (s.kind === 'crop' ? 'is-crop' : 'is-resource is-' + s.category + (s.element ? ' is-el-' + s.element : '')) + ' is-' + s.status;
        el.innerHTML = s.kind === 'crop' ? '<i class="fas fa-leaf" aria-hidden="true"></i>' : '<span></span>';
        if (s.kind !== 'crop') el.querySelector('span').textContent = s.element || s.symbol || '•';
        el.title = s.name + ' · ' + (s.resource || '');
      }
      el.setAttribute('aria-label', el.title);
      el.addEventListener('click', e => {
        e.stopPropagation();
        if (!this.picking) this.onSelect(s);
      });
      const opts = s.kind === 'port' ? {
        element: el,
        anchor: 'left',
        offset: [-13, 0]
      } : {
        element: el
      };
      this.markers[s.id] = {
        item: s,
        el,
        marker: new gl.Marker(opts).setLngLat([s.lng, s.lat]).addTo(this.map)
      };
    }
    if (this.map && this.map.getSource('rm-lanes')) this.map.getSource('rm-lanes').setData(this.lanesData());
    if (this.map && this.map.getSource('rm-railproj')) this.map.getSource('rm-railproj').setData(this.railData());
    this.applyMarkers();
  }

  /** Which sites are visible (from the filters and search) */
  setVisible(ids) {
    this.visible = new Set(ids);
    this.applyMarkers();
  }
  applyMarkers() {
    for (const [id, m] of Object.entries(this.markers)) {
      const k = m.item.kind;
      const layerOn = k === 'port' ? this.layers.ports : k === 'facility' ? this.layers.facilities : this.layers.resources;
      m.el.style.display = layerOn && this.inView(m.item.country) && (k === 'port' || this.visible.has(id)) ? '' : 'none';
    }
  }

  /** Highlight a site, label it and move the map to it */
  select(site) {
    for (const m of Object.values(this.markers)) m.el.classList.toggle('is-selected', !!site && m.item.dbId === site.dbId);
    if (this.popup) this.popup.remove();
    if (!site || !this.map) return;
    this.popup = new this.gl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 18,
      className: 'RMLabel'
    }).setLngLat([site.lng, site.lat]).setText(site.name + (site.resource ? ' · ' + site.resource : '')).addTo(this.map);
    this.map.flyTo({
      center: [site.lng, site.lat],
      zoom: Math.max(this.map.getZoom(), 6.2),
      speed: 0.9
    });
  }

  /**
   * Editors: click the map to place a point, or click along a route to draw a line.
   * onChange receives the point {lat, lng} or the line [[lng, lat], ...] after every click.
   */
  startPick(mode, onChange, start) {
    if (start === void 0) {
      start = null;
    }
    this.stopPick();
    this.picking = {
      mode,
      onChange,
      points: mode === 'line' ? (start || []).slice() : []
    };
    if (mode === 'point' && start) this.picking.points = [[start.lng, start.lat]];
    this.drawDraft();
    this.map.getCanvas().style.cursor = 'crosshair';
    this.pickHandler = e => {
      const p = [Math.round(e.lngLat.lng * 1e5) / 1e5, Math.round(e.lngLat.lat * 1e5) / 1e5];
      if (this.picking.mode === 'point') this.picking.points = [p];else this.picking.points.push(p);
      this.drawDraft();
      this.picking.onChange(this.picking.mode === 'point' ? {
        lng: p[0],
        lat: p[1]
      } : this.picking.points.slice());
    };
    this.map.on('click', this.pickHandler);
  }

  /** Remove the last point of a line being drawn */
  undoPoint() {
    if (!this.picking || this.picking.mode !== 'line') return;
    this.picking.points.pop();
    this.drawDraft();
    this.picking.onChange(this.picking.points.slice());
  }
  stopPick() {
    if (this.pickHandler && this.map) this.map.off('click', this.pickHandler);
    this.pickHandler = null;
    this.picking = null;
    if (this.map) {
      this.map.getCanvas().style.cursor = '';
      this.drawDraft();
    }
  }
  drawDraft() {
    const src = this.map && this.map.getSource('rm-draft');
    if (!src) return;
    const pts = this.picking && this.picking.points || [];
    const features = pts.map(p => ({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: p
      }
    }));
    if (pts.length > 1) features.push({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: pts
      }
    });
    src.setData({
      type: 'FeatureCollection',
      features
    });
  }

  /** A plan's features (or none) as map data */
  planData() {
    const f = this.planFeatures || {
      lines: [],
      notes: []
    };
    return {
      type: 'FeatureCollection',
      features: [...(f.lines || []).map(l => ({
        type: 'Feature',
        properties: {
          type: l.type
        },
        geometry: {
          type: 'LineString',
          coordinates: l.coords
        }
      })), ...(f.notes || []).map(n => ({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [n.lng, n.lat]
        }
      }))]
    };
  }

  /** Show a plan's lines and notes (null clears them) */
  setPlan(features) {
    this.planFeatures = features;
    const src = this.map && this.map.getSource('rm-plan');
    if (src) src.setData(this.planData());
    (this.noteMarkers || []).forEach(mk => mk.remove());
    this.noteMarkers = (features && features.notes || []).map(n => {
      const el = document.createElement('div');
      el.className = 'RMPlanNote';
      el.textContent = n.text;
      return new this.gl.Marker({
        element: el,
        anchor: 'bottom',
        offset: [0, -10]
      }).setLngLat([n.lng, n.lat]).addTo(this.map);
    });
  }

  /** The current view, for saving in a plan (bearing 180 = south up) */
  getView() {
    const c = this.map.getCenter();
    return {
      center: {
        lat: Math.round(c.lat * 1e5) / 1e5,
        lng: Math.round(c.lng * 1e5) / 1e5
      },
      zoom: Math.round(this.map.getZoom() * 100) / 100,
      bearing: Math.round(this.map.getBearing())
    };
  }
  setView(view) {
    if (this.map && view && view.center) this.map.jumpTo({
      center: [view.center.lng, view.center.lat],
      zoom: view.zoom || 5,
      bearing: view.bearing || 0
    });
  }

  /** South up: the map turned 180°. Place names and markers stay upright. */
  setSouthUp(on) {
    if (this.map) this.map.easeTo({
      bearing: on ? 180 : 0,
      duration: 700
    });
  }
  isSouthUp() {
    return !!this.map && Math.abs(Math.abs(this.map.getBearing()) - 180) < 1;
  }

  /**
   * The map as an image for link previews: waits until it has finished drawing, then crops to
   * 1200x630. Note labels aren't part of the map canvas, so the notes show as dots.
   */
  capture() {
    return new Promise((resolve, reject) => {
      const snap = () => {
        try {
          const src = this.map.getCanvas();
          const out = document.createElement('canvas');
          out.width = 1200;
          out.height = 630;
          const scale = Math.max(1200 / src.width, 630 / src.height);
          const w = 1200 / scale;
          const h = 630 / scale;
          out.getContext('2d').drawImage(src, (src.width - w) / 2, (src.height - h) / 2, w, h, 0, 0, 1200, 630);
          resolve(out.toDataURL('image/jpeg', 0.85));
        } catch (e) {
          reject(e);
        }
      };
      // Wait for the map to settle, but never more than 1.5s: with the river animation running it
      // keeps redrawing and may never report "idle"
      let done = false;
      const once = () => {
        if (!done) {
          done = true;
          snap();
        }
      };
      if (this.map.loaded()) once();else {
        this.map.once('idle', once);
        setTimeout(once, 1500);
      }
    });
  }

  /** Fit the map to some sites (after a search or filter) */
  /** Show the full-screen button's state: expand, or exit */
  setFullscreen(on) {
    const b = this.fullscreenBtn;
    if (!b) return;
    b.innerHTML = '<i class="fas ' + (on ? 'fa-compress' : 'fa-expand') + '" aria-hidden="true"></i>';
    b.title = on ? 'Exit full screen (Esc)' : 'Full screen';
    b.setAttribute('aria-label', b.title);
  }
  areaData() {
    return {
      type: 'FeatureCollection',
      features: this.area ? [{
        type: 'Feature',
        properties: {},
        geometry: this.area.geometry
      }] : []
    };
  }

  /** The whole world with the chosen place cut out (each of its outlines becomes a hole) */
  maskData() {
    const g = this.area && this.area.geometry;
    if (!g) return {
      type: 'FeatureCollection',
      features: []
    };
    const polygons = g.type === 'MultiPolygon' ? g.coordinates : [g.coordinates];
    const world = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [world, ...polygons.map(p => p[0])]
        }
      }]
    };
  }

  /** Outline a state or LGA, dim everything around it and zoom to it (null clears it) */
  showArea(area) {
    this.area = area || null;
    if (this.map && this.map.getSource('rm-area')) this.map.getSource('rm-area').setData(this.areaData());
    if (this.map && this.map.getSource('rm-area-mask')) this.map.getSource('rm-area-mask').setData(this.maskData());
    if (this.map && area && area.bbox) {
      const [x1, y1, x2, y2] = area.bbox;
      this.map.fitBounds([[x1, y1], [x2, y2]], {
        padding: 50,
        maxZoom: 10,
        duration: 900
      });
    }
  }
  fit(sites) {
    if (!this.map || !sites.length) return;
    if (sites.length === 1) return this.map.flyTo({
      center: [sites[0].lng, sites[0].lat],
      zoom: 6.5
    });
    const b = new this.gl.LngLatBounds();
    sites.forEach(s => b.extend([s.lng, s.lat]));
    this.map.fitBounds(b, {
      padding: 80,
      maxZoom: 7,
      duration: 900
    });
  }

  /** River and shipping flow: dashes moving along the lines */
  setAnimate(on) {
    this.animating = on;
    this.applyLayers();
    clearInterval(this.timer);
    if (!on) return;
    this.timer = setInterval(() => {
      if (!this.map || !this.map.isStyleLoaded() || document.hidden) return;
      this.step = (this.step + 1) % DASHES.length;
      ['rm-rivers-flow', 'rm-flow-anim', 'rm-shipping'].forEach(id => this.map.getLayer(id) && this.map.setPaintProperty(id, 'line-dasharray', DASHES[this.step]));
    }, 80);
  }
  destroy() {
    clearInterval(this.timer);
    clearTimeout(this.watchdog);
    if (this.resizer) this.resizer.disconnect();
    if (this.map) this.map.remove();
    this.map = null;
  }
}

/**
 * Nearest named rivers and lakes to a site, read from the map's own data (OpenStreetMap via
 * MapTiler) once the area has loaded. @return Promise<[{name, kind, km}]>
 */
MapController.prototype.nearestWater = function (site) {
  const map = this.map;
  if (!map) return Promise.resolve([]);
  const R = 6371;
  const rad = d => d * Math.PI / 180;
  const dist = (lng, lat) => {
    const dLat = rad(lat - site.lat);
    const dLng = rad(lng - site.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(site.lat)) * Math.cos(rad(lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  const points = g => g.type === 'Point' ? [g.coordinates] : g.type === 'LineString' || g.type === 'MultiPoint' ? g.coordinates : g.type === 'MultiLineString' ? g.coordinates.flat() : [];
  return new Promise(resolve => {
    const read = () => {
      const best = {};
      const consider = (features, kind) => {
        for (const f of features) {
          const name = f.properties && (f.properties['name:en'] || f.properties.name);
          if (!name) continue;
          for (const [lng, lat] of points(f.geometry)) {
            const d = dist(lng, lat);
            if (!best[name] || d < best[name].km) best[name] = {
              name,
              kind,
              km: d
            };
          }
        }
      };
      try {
        consider(map.querySourceFeatures('rm-omt', {
          sourceLayer: 'waterway',
          filter: ['==', ['get', 'class'], 'river']
        }), 'river');
        consider(map.querySourceFeatures('rm-omt', {
          sourceLayer: 'water_name',
          filter: ['==', ['get', 'class'], 'lake']
        }), 'lake');
      } catch (e) {
        return resolve([]);
      }
      resolve(Object.values(best).filter(w => w.km <= 150).sort((a, b) => a.km - b.km).slice(0, 4).map(w => ({
        ...w,
        km: Math.max(1, Math.round(w.km))
      })));
    };
    // wait for the flight to the site to finish and its tiles to load
    map.once('idle', read);
    setTimeout(read, 6000); // fallback if the map never goes idle
  });
};